/**
 * Market Data Service - Responsible for fetching market data from Finnhub API
 * This layer only handles: viewing markets, fetching market data, format conversion, caching
 * No trading execution logic
 */

import axios from 'axios';
import { supabase } from '../lib/supabaseClient';

// 使用相对路径，依赖React代理
// 开发环境：/api/* → http://127.0.0.1:8889/api/* (通过package.json proxy)
// 生产环境：通过环境变量REACT_APP_API_BASE_URL配置
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

// 调试：输出环境变量（仅开发环境）
if (process.env.NODE_ENV !== 'production') {
  console.log('[调试] API_BASE_URL:', API_BASE_URL);
  console.log('[调试] REACT_APP_API_BASE_URL:', process.env.REACT_APP_API_BASE_URL);
  console.log('[调试] REACT_APP_API_URL:', process.env.REACT_APP_API_URL);
  console.log('[调试] NODE_ENV:', process.env.NODE_ENV);
}

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,  // 增加到30秒，适应更多股票数据
  headers: {
    'Content-Type': 'application/json',
  },
});

// Check if JWT is expired (decode exp claim, with 60s buffer)
function isTokenExpired(token: string): boolean {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return !payload.exp || Date.now() >= (payload.exp * 1000 - 60000);
  } catch {
    return true;
  }
}

// Attach Supabase access token so backend can resolve per-user config, refreshing if needed
const attachSupabaseToken = async (config: any) => {
  let { data: { session } } = await supabase.auth.getSession();
  if (session?.access_token && isTokenExpired(session.access_token)) {
    try {
      const { data: refreshed } = await supabase.auth.refreshSession();
      if (refreshed.session) session = refreshed.session;
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') console.warn('[Auth] Session refresh failed:', e);
    }
  }
  if (session?.access_token) {
    config.headers.Authorization = `Bearer ${session.access_token}`;
  }
  return config;
};

api.interceptors.request.use(attachSupabaseToken);

// 添加请求拦截器用于调试（仅开发环境）
api.interceptors.request.use(
  (config) => {
    if (process.env.NODE_ENV !== 'production') {
      console.log('[axios请求拦截器] 请求配置:', {
        url: config.url,
        baseURL: config.baseURL,
        method: config.method,
        params: config.params,
        headers: config.headers,
        完整URL: (config.baseURL || '') + (config.url || '')
      });
    }
    return config;
  },
  (error) => {
    if (process.env.NODE_ENV !== 'production') console.error('[axios请求拦截器] 请求错误:', error);
    return Promise.reject(error);
  }
);

// 添加响应拦截器用于调试（仅开发环境）
api.interceptors.response.use(
  (response) => {
    if (process.env.NODE_ENV !== 'production') {
      console.log('[axios响应拦截器] 响应成功:', {
        status: response.status,
        statusText: response.statusText,
        url: response.config.url,
        dataKeys: Object.keys(response.data)
      });
    }
    return response;
  },
  (error) => {
    if (process.env.NODE_ENV !== 'production') console.error('[axios响应拦截器] 响应错误:', {
      message: error.message,
      code: error.code,
      response: error.response?.data,
      status: error.response?.status,
      config: error.config ? {
        url: error.config.url,
        baseURL: error.config.baseURL,
        method: error.config.method,
        params: error.config.params
      } : '无配置信息'
    });
    return Promise.reject(error);
  }
);

// ========== Type Definitions ==========

export interface StockData {
  symbol: string;
  name: string | null;
  price: number | null;
  change: number | null;
  changePercent: number | null;
  changePct?: number | null;  // 新增：前端scanner使用的字段名
  volume: number | null;
  avgVolume?: number | null;  // 后端返回的是avgVolume字段
  marketCap: number | null;
  sector: string | null;
  industry: string | null;
  currency: string;
  dayOpen?: number | null;
  dayHigh: number | null;
  dayLow: number | null;
  previousClose: number | null;
  dataSource: string;
  priceSource?: string;
  timestamp: string;
  error?: string;
  peRatio?: number | null;
  dividendYield?: number | null;
  yearHigh?: number | null;
  yearLow?: number | null;
  beta?: number | null;
  earningsDate?: string | null;
}

export interface MarketRiskConstituent {
  symbol: string;
  name: string;
  exchange: string;
  price: number;
  previousClose: number;
  changePct: number;
  dollarVolume: number;
  dayBarTime?: string | null;
}

export interface MarketRiskSnapshotResponse {
  success: boolean;
  snapshot: {
    riskScore: number;
    riskLevel: 'normal' | 'guarded' | 'elevated' | 'high';
    regime: 'risk_on' | 'constructive' | 'mixed' | 'defensive' | 'risk_off';
    universeCount: number;
    validCount: number;
    coveragePct: number;
    advancing: number;
    declining: number;
    unchanged: number;
    advancingPct: number;
    decliningPct: number;
    advanceDeclineRatio: number;
    equalWeightChangePct: number;
    liquidityWeightedChangePct: number;
    medianChangePct: number;
    downTwoPct: number;
    upTwoPct: number;
    dispersionPct: number;
    p10ChangePct: number;
    p90ChangePct: number;
    distribution: Array<{ label: string; count: number }>;
    method: string;
    source: string;
    components: Record<string, number>;
  };
  benchmarks: MarketRiskConstituent[];
  sectorEtfs: MarketRiskConstituent[];
  exchangeBreadth: Array<{
    name: string;
    total: number;
    advancing: number;
    declining: number;
    unchanged: number;
  }>;
  movers: MarketRiskConstituent[];
  gainers?: MarketRiskConstituent[];
  losers?: MarketRiskConstituent[];
  themeBreadth?: Array<{
    key: string;
    label: string;
    total: number;
    advancing: number;
    declining: number;
    unchanged: number;
    averageChangePct: number;
    advanceDeclineRatio: number;
    leaders: Array<{ symbol: string; changePct: number }>;
  }>;
  asOf: string;
  generatedAt: string;
  universeSource: string;
  rawAssetCount: number;
  snapshotAvailable: number;
  snapshotErrorCount: number;
  cache?: { status: string; ageSeconds: number };
  warning?: string;
}

export interface HistoricalDataPoint {
  timestamp: number;
  time: string;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
  sma20?: number;
  sma50?: number;
  ema12?: number;
  ema26?: number;
  rsi?: number;
}

export interface HistoricalDataResponse {
  symbol: string;
  interval: string;
  range: string;
  data: HistoricalDataPoint[];
  count: number;
  dataSource: string;
  timestamp: number;
  error?: string;
  errorType?: 'no_bars' | 'config_required' | 'auth_required' | 'api_error' | 'rate_limited';
  snapshotAvailable?: boolean;
  warning?: string;
  isSimulated?: boolean;
  basePrice?: number;
  priceRange?: string;
  fallbackUsed?: boolean;
  requestedTimeframe?: string;
  displayedTimeframe?: string;
  message?: string;
}

export interface SearchResult {
  symbol: string;
  name: string;
  type: string;
  region: string;
  currency: string;
}

// ========== Timeframe Configuration ==========

export const TIMEFRAMES: Record<string, { interval: string; range: string; dataPoints: number; label: string }> = {
  '1D': { interval: '5min', range: '1day', dataPoints: 78, label: '1 Day' },    // 5分钟粒度，78个数据点（6.5小时×12个5分钟段）
  '1W': { interval: '1h', range: '1week', dataPoints: 40, label: '1 Week' },    // 1小时粒度，40个数据点（5天×8个交易小时）
  '1M': { interval: '1day', range: '1month', dataPoints: 22, label: '1 Month' }, // 日线粒度，22个交易日
  '3M': { interval: '1day', range: '3month', dataPoints: 66, label: '3 Months' }, // 日线粒度，66个交易日
  '1Y': { interval: '1day', range: '1year', dataPoints: 252, label: '1 Year' },   // 日线粒度，252个交易日
};

// ========== Utility Functions ==========

/**
 * Format currency for display
 */
export const formatCurrency = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '--';
  
  const num = Number(value);
  if (isNaN(num)) return '--';
  
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
};

/**
 * Format percentage for display
 */
export const formatPercent = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '--';
  
  const num = Number(value);
  if (isNaN(num)) return '--';
  
  if (num === 0) return '0.00%';
  return `${num > 0 ? '+' : '-'}${Math.abs(num).toFixed(2)}%`;
};

/**
 * Format volume with K/M/B units
 * Rules:
 * - < 1,000 → original value
 * - < 1,000,000 → K (thousands)
 * - < 1,000,000,000 → M (millions)
 * - >= 1,000,000,000 → B (billions)
 */
export const formatVolume = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '--';
  
  const num = Number(value);
  if (isNaN(num)) return '--';
  
  // Handle negative volume (shouldn't happen, but just in case)
  const absValue = Math.abs(num);
  
  if (absValue < 1000) {
    return num.toString();
  } else if (absValue < 1000000) {
    return `${(num / 1000).toFixed(1)}K`;
  } else if (absValue < 1000000000) {
    return `${(num / 1000000).toFixed(1)}M`;
  } else {
    return `${(num / 1000000000).toFixed(1)}B`;
  }
};

/**
 * Safe number conversion (returns 0 for null/undefined/NaN)
 */
export const safeNumber = (value: any): number => {
  if (value === null || value === undefined) return 0;
  
  const num = Number(value);
  return isNaN(num) ? 0 : num;
};

/**
 * Safe toFixed with null handling
 */
export const safeToFixed = (value: number | null | undefined, decimals: number = 2): string => {
  if (value === null || value === undefined) return '--';
  
  const num = Number(value);
  if (isNaN(num)) return '--';
  
  return num.toFixed(decimals);
};

/**
 * Calculate Simple Moving Average
 */
export const calculateSMA = (data: number[], period: number): number[] => {
  const sma: number[] = [];
  
  for (let i = 0; i < data.length; i++) {
    if (i < period - 1) {
      sma.push(NaN);
    } else {
      const sum = data.slice(i - period + 1, i + 1).reduce((a, b) => a + b, 0);
      sma.push(sum / period);
    }
  }
  
  return sma;
};

/**
 * Calculate Exponential Moving Average
 */
export const calculateEMA = (data: number[], period: number): number[] => {
  const ema: number[] = [];
  const multiplier = 2 / (period + 1);
  
  // First EMA is SMA
  let emaValue = data.slice(0, period).reduce((a, b) => a + b, 0) / period;
  
  for (let i = 0; i < data.length; i++) {
    if (i < period - 1) {
      ema.push(NaN);
    } else if (i === period - 1) {
      ema.push(emaValue);
    } else {
      emaValue = (data[i] - emaValue) * multiplier + emaValue;
      ema.push(emaValue);
    }
  }
  
  return ema;
};

/**
 * Calculate Relative Strength Index
 */
export const calculateRSI = (data: number[], period: number = 14): number[] => {
  const rsi: number[] = [];
  
  if (data.length < period + 1) {
    return Array(data.length).fill(NaN);
  }
  
  const gains: number[] = [];
  const losses: number[] = [];
  
  // Calculate price changes
  for (let i = 1; i < data.length; i++) {
    const change = data[i] - data[i - 1];
    gains.push(change > 0 ? change : 0);
    losses.push(change < 0 ? Math.abs(change) : 0);
  }
  
  // Calculate RSI
  for (let i = period; i < gains.length; i++) {
    const avgGain = gains.slice(i - period, i).reduce((a, b) => a + b, 0) / period;
    const avgLoss = losses.slice(i - period, i).reduce((a, b) => a + b, 0) / period;
    
    if (avgLoss === 0) {
      rsi.push(100);
    } else {
      const rs = avgGain / avgLoss;
      rsi.push(100 - (100 / (1 + rs)));
    }
  }
  
  // Pad beginning with NaN
  return [...Array(period).fill(NaN), ...rsi];
};

// ========== Core API Functions ==========

/**
 * Get dashboard system status (per-user config state)
 */
export const getDashboardStatus = async (): Promise<{
  marketData: string;
  quoteFeed: string;
  brokerConnection: string;
  environment: string;
}> => {
  try {
    const response = await api.get('/dashboard/status');
    if (response.data) {
      return response.data;
    }
    return { marketData: 'ERROR', quoteFeed: 'ERROR', brokerConnection: 'UNKNOWN', environment: 'Unknown' };
  } catch {
    return { marketData: 'ERROR', quoteFeed: 'ERROR', brokerConnection: 'UNKNOWN', environment: 'Unknown' };
  }
};

export const getMarketRiskSnapshot = async (forceRefresh = false): Promise<MarketRiskSnapshotResponse> => {
  const response = await api.get('/market/risk-snapshot', {
    params: forceRefresh ? { refresh: 'true', limit: 1500 } : { limit: 1500 },
    timeout: 120000,
  });
  if (!response.data?.success || !response.data?.snapshot) {
    throw new Error(response.data?.error || 'Broad-market risk snapshot is unavailable.');
  }
  return response.data as MarketRiskSnapshotResponse;
};

/**
 * Get list of stocks for Market page
 * @param symbols Optional array of symbols to filter (if provided, returns data for those symbols only)
 */
export const getStocks = async (
  symbols?: string[],
  dashboard?: boolean,
  forceRefresh?: boolean,
): Promise<StockData[]> => {
  try {
    const params: any = {};
    
    // 如果提供了symbols参数，传递给后端
    if (symbols && symbols.length > 0) {
      params.symbols = symbols.join(',');
    }
    
    // Dashboard请求使用轻量级模式
    if (dashboard) {
      params.dashboard = 'true';
    }

    if (forceRefresh) {
      params.refresh = 'true';
    }
    
    if (process.env.NODE_ENV !== 'production') {
      console.log(`[marketDataService调试] 开始请求股票数据`);
      console.log(`[marketDataService调试] 参数: symbols=${symbols?.join(',') || '无'}, dashboard=${dashboard}`);
      console.log(`[marketDataService调试] api实例配置:`, {
        baseURL: api.defaults.baseURL,
        timeout: api.defaults.timeout,
        headers: api.defaults.headers
      });
      console.log(`[marketDataService调试] 完整请求URL: ${api.defaults.baseURL}/market/stocks`);
      console.log(`[marketDataService调试] 请求参数:`, params);
    }

    const response = await api.get('/market/stocks', { params });

    if (process.env.NODE_ENV !== 'production') {
      console.log(`[前端调试] 收到响应:`, {
        status: response.status,
        dataKeys: Object.keys(response.data),
        hasStocks: !!response.data.stocks,
        stocksCount: response.data.stocks?.length || 0,
        responseStructure: response.data
      });
    }

    // Check if backend reports auth or config issues
    if (response.data?.configStatus === 'auth_required') {
      const err: any = new Error('Authentication required: please sign in again.');
      err.code = 'AUTH_REQUIRED';
      throw err;
    }
    if (response.data?.configStatus === 'config_required') {
      const err: any = new Error('Configuration required: please configure your API keys in Settings.');
      err.code = 'CONFIG_REQUIRED';
      throw err;
    }

    if (response.data && response.data.stocks) {
      const stocks = response.data.stocks.map((stock: any) => ({
        ...stock,
        // 后端已经返回正确的字段名：dayHigh, dayLow, previousClose
        dayHigh: stock.dayHigh !== undefined ? stock.dayHigh : null,
        dayLow: stock.dayLow !== undefined ? stock.dayLow : null,
        previousClose: stock.previousClose !== undefined ? stock.previousClose : null,
        // 保持其他字段
        dataSource: stock.dataSource || response.data.source || 'Finnhub',
        timestamp: new Date().toISOString(),
      }));

      if (process.env.NODE_ENV !== 'production') console.log(`[前端调试] 成功处理 ${stocks.length} 支股票`);
      return stocks;
    }

    if (process.env.NODE_ENV !== 'production') console.warn(`[前端调试] 响应中没有 stocks 字段，返回空数组`);
    return [];
  } catch (error: any) {
    if (process.env.NODE_ENV !== 'production') console.error('[前端调试] Failed to fetch stocks:', error);
    if (process.env.NODE_ENV !== 'production') console.error('[前端调试] 错误详情:', {
      message: error.message,
      code: error.code,
      response: error.response?.data,
      status: error.response?.status
    });
    // Preserve config/auth codes for Dashboard to handle
    if (error.code === 'CONFIG_REQUIRED' || error.code === 'AUTH_REQUIRED') {
      throw error;
    }
    throw new Error(error.response?.data?.error || error.message || 'Failed to fetch stocks');
  }
};

/**
 * Get single stock detailed information
 */
export const getStockData = async (symbol: string): Promise<StockData> => {
  try {
    const response = await api.get(`/market/stock/${symbol}`);
    
    if (response.data && !response.data.error) {
      // 后端已经返回正确的字段名：dayHigh, dayLow, previousClose, yearHigh, yearLow
      const stock = response.data;
      return {
        ...stock,
        // 确保字段存在，如果后端返回null/undefined，设置为null
        dayHigh: stock.dayHigh !== undefined ? stock.dayHigh : null,
        dayLow: stock.dayLow !== undefined ? stock.dayLow : null,
        previousClose: stock.previousClose !== undefined ? stock.previousClose : null,
        avgVolume: stock.avgVolume !== undefined ? stock.avgVolume : null,  // ✅ 新增：处理avgVolume
        yearHigh: stock.yearHigh !== undefined ? stock.yearHigh : null,  // ✅ 新增：处理yearHigh
        yearLow: stock.yearLow !== undefined ? stock.yearLow : null,    // ✅ 新增：处理yearLow
        // 确保changePct字段存在，优先使用后端返回的changePct，否则使用changePercent
        changePct: stock.changePct !== undefined ? stock.changePct : (stock.changePercent !== undefined ? stock.changePercent : null),
        dataSource: stock.dataSource || 'Finnhub',
        timestamp: new Date().toISOString(),
      } as StockData;
    }
    
    throw new Error(response.data?.error || `Stock ${symbol} not found`);
  } catch (error: any) {
    if (process.env.NODE_ENV !== 'production') console.error(`Failed to fetch stock ${symbol} data:`, error);
    throw new Error(error.response?.data?.error || error.message || `Failed to fetch stock ${symbol} data`);
  }
};

/**
 * Get stock historical price data
 */
export const getStockHistory = async (
  symbol: string,
  timeframe: string = '1M',
  options?: { adjustedData?: boolean; session?: 'regular' | 'extended' },
): Promise<HistoricalDataResponse> => {
  try {
    const config = TIMEFRAMES[timeframe] || TIMEFRAMES['1M'];

    const response = await api.get(`/market/history/${symbol}`, {
      params: {
        interval: config.interval,
        range: config.range,
        adjustment: options?.adjustedData === false ? 'raw' : 'all',
        session: options?.session || 'regular',
      },
    });

    if (response.data) {
      const data = response.data;

      // For no_bars errors, return the response with error info (don't throw)
      if (data.errorType === 'no_bars' || (data.success === false && (!data.data || data.data.length === 0))) {
        return {
          symbol,
          interval: config.interval,
          range: config.range,
          data: [],
          count: 0,
          dataSource: data.dataSource || 'Alpaca',
          timestamp: data.timestamp || Date.now(),
          error: data.error || 'No historical bars available',
          errorType: data.errorType || 'no_bars',
          snapshotAvailable: data.snapshotAvailable !== false,
        } as HistoricalDataResponse;
      }

      // Check for other errors
      if (data.error) {
        const source = data.dataSource || 'Alpaca';
        throw new Error(`${source} API error: ${data.error}`);
      }

      return {
        ...data,
        symbol,
        interval: config.interval,
        range: config.range,
        dataSource: data.dataSource || data.source || 'Unknown',
        count: data.count || (data.data ? data.data.length : 0),
        warning: data.warning,
        isSimulated: data.isSimulated,
        basePrice: data.basePrice,
        priceRange: data.priceRange,
      } as HistoricalDataResponse;
    } else {
      throw new Error('Empty response from server');
    }

  } catch (error: any) {
    // Distinguish error types for the caller
    const msg = error.message || '';
    if (error.response?.status === 401 || msg.includes('401')) {
      throw new Error('auth_required: ' + msg);
    }
    if (error.response?.status === 429 || msg.includes('429') || msg.includes('rate')) {
      throw new Error('rate_limited: ' + msg);
    }
    if (process.env.NODE_ENV !== 'production') console.error(`Failed to fetch ${symbol} historical data:`, error);
    throw error;
  }
};

/**
 * Search stocks by symbol or name
 * @param query Search query string
 * @param limit Optional limit for number of results
 */
export const searchStocks = async (query: string, limit?: number): Promise<SearchResult[]> => {
  try {
    const response = await api.get('/market/search', {
      params: { q: query },
    });

    if (response.data && response.data.results) {
      let results = response.data.results;

      // Apply limit if provided
      if (limit && limit > 0) {
        results = results.slice(0, limit);
      }

      return results;
    }

    return [];
  } catch (error: any) {
    if (process.env.NODE_ENV !== 'production') console.error('Failed to search stocks:', error);
    throw new Error(error.response?.data?.error || error.message || 'Failed to search stocks');
  }
};

/**
 * Search stocks and return full StockData (with prices from Alpaca/Finnhub).
 * Backend returns complete stock objects, not just symbol/name pairs.
 * @param query Search query string
 */
export const searchStockData = async (query: string): Promise<{ stocks: StockData[]; status: string; message?: string }> => {
  try {
    const response = await api.get('/market/search', {
      params: { q: query },
    });

    const data = response.data;
    const status = data?.status || 'unknown';

    if (status === 'auth_required') {
      const err: any = new Error('Authentication required: please sign in again.');
      err.code = 'AUTH_REQUIRED';
      throw err;
    }
    if (status === 'config_required') {
      const err: any = new Error('Configuration required: please configure your API keys in Settings.');
      err.code = 'CONFIG_REQUIRED';
      throw err;
    }

    if (data && data.results && data.results.length > 0) {
      const stocks: StockData[] = data.results.map((stock: any) => ({
        symbol: stock.symbol,
        name: stock.name || stock.symbol,
        price: stock.price ?? null,
        change: stock.change ?? null,
        changePercent: stock.changePercent ?? null,
        volume: stock.volume ?? null,
        marketCap: stock.marketCap ?? null,
        sector: stock.sector ?? null,
        industry: stock.industry ?? null,
        currency: stock.currency || 'USD',
        dayOpen: stock.open ?? stock.dayOpen ?? null,
        dayHigh: stock.dayHigh ?? null,
        dayLow: stock.dayLow ?? null,
        previousClose: stock.previousClose ?? null,
        dataSource: stock.dataSource || 'Alpaca',
        priceSource: stock.priceSource || 'unknown',
        timestamp: new Date().toISOString(),
      }));
      return { stocks, status: 'ok' };
    }

    return {
      stocks: [],
      status: status,
      message: data?.message || 'No matching company or symbol found. Please check your input.',
    };
  } catch (error: any) {
    if (error.code === 'CONFIG_REQUIRED' || error.code === 'AUTH_REQUIRED') {
      throw error;
    }
    if (process.env.NODE_ENV !== 'production') console.error('Search failed:', error);
    throw new Error(error.response?.data?.error || error.message || 'Search failed. Please check API configuration or try again.');
  }
};

/**
 * Get batch stock data for multiple symbols
 */
export const getBatchStockData = async (symbols: string[]): Promise<StockData[]> => {
  try {
    const promises = symbols.map(symbol => getStockData(symbol).catch(() => null));
    const results = await Promise.all(promises);
    
    return results.filter((result): result is StockData => result !== null);
  } catch (error: any) {
    if (process.env.NODE_ENV !== 'production') console.error('Failed to fetch batch stock data:', error);
    throw new Error(error.response?.data?.error || error.message || 'Failed to fetch batch stock data');
  }
};

// ========== User Market Symbols API ==========

export const getUserMarketSymbols = async (): Promise<{ symbols: string[]; status: string }> => {
  try {
    const response = await api.get('/market/user-symbols');
    return {
      symbols: response.data?.symbols || [],
      status: response.data?.status || 'unknown',
    };
  } catch (error: any) {
    if (process.env.NODE_ENV !== 'production') console.error('Failed to get user market symbols:', error);
    return { symbols: [], status: 'error' };
  }
};

export const addUserMarketSymbols = async (symbols: string[]): Promise<{ symbols: string[]; added: string[]; status: string; error?: string }> => {
  try {
    const response = await api.post('/market/user-symbols', { symbols });
    return {
      symbols: response.data?.symbols || [],
      added: response.data?.added || [],
      status: response.data?.status || 'unknown',
      error: response.data?.error,
    };
  } catch (error: any) {
    if (process.env.NODE_ENV !== 'production') console.error('Failed to add user market symbols:', error);
    return { symbols: [], added: [], status: 'error', error: error.message };
  }
};

export const deleteUserMarketSymbol = async (symbol: string): Promise<{ symbols: string[]; removed: string; status: string }> => {
  try {
    const response = await api.delete(`/market/user-symbols/${symbol}`);
    return {
      symbols: response.data?.symbols || [],
      removed: response.data?.removed || symbol,
      status: response.data?.status || 'unknown',
    };
  } catch (error: any) {
    if (process.env.NODE_ENV !== 'production') console.error('Failed to delete user market symbol:', error);
    return { symbols: [], removed: symbol, status: 'error' };
  }
};

// ========== Service Object ==========

const marketDataService = {
  // Core API
  getStocks,
  getStockData,
  getStockHistory,
  searchStocks,
  searchStockData,
  getBatchStockData,
  getDashboardStatus,

  // User Market Symbols API
  getUserMarketSymbols,
  addUserMarketSymbols,
  deleteUserMarketSymbol,

  // Utility functions
  formatCurrency,
  formatPercent,
  formatVolume,
  safeNumber,
  safeToFixed,
  calculateSMA,
  calculateEMA,
  calculateRSI,

  // Constants
  TIMEFRAMES,
};

export default marketDataService;
